import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { prayerNames } from '../../data/prayerData';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';
const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Prayer accent colours
const PRAYER_COLORS = {
  Fajr:    { bar: '#818cf8', bg: 'bg-indigo-500', light: 'bg-indigo-100', text: 'text-indigo-600', darkText: 'text-indigo-400' },
  Dhuhr:   { bar: '#f59e0b', bg: 'bg-amber-400',  light: 'bg-amber-100',  text: 'text-amber-600',  darkText: 'text-amber-400' },
  Asr:     { bar: '#f97316', bg: 'bg-orange-400', light: 'bg-orange-100', text: 'text-orange-600', darkText: 'text-orange-400' },
  Maghrib: { bar: '#ec4899', bg: 'bg-rose-400',   light: 'bg-rose-100',   text: 'text-rose-600',   darkText: 'text-rose-400' },
  Isha:    { bar: '#6366f1', bg: 'bg-violet-500', light: 'bg-violet-100', text: 'text-violet-600', darkText: 'text-violet-400' },
};

/**
 * PrayerBreakdown — accepts optional alltime prop from parent (TrackerPage).
 * Falls back to local computation if not provided.
 */
const PrayerBreakdown = ({ alltime: alltimeProp }) => {
  const { t } = useTranslation();
  const { isDarkMode, language, isRTL } = useTheme();
  const { isAuthenticated } = useAuth();
  const [alltime, setAlltime] = useState(alltimeProp || null);

  useEffect(() => {
    if (alltimeProp) { setAlltime(alltimeProp); return; }
    if (!isAuthenticated) return;
    fetch(`${API_URL}/api/tracker/alltime`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setAlltime(d); })
      .catch(() => {});
  }, [isAuthenticated, alltimeProp]);

  // Build breakdown from localStorage this month
  const today = new Date();
  const year = today.getFullYear();
  const monthNum = today.getMonth() + 1;
  const daysInMonth = new Date(year, monthNum, 0).getDate();

  const localRates = {};
  let totalDays = 0;
  for (let d = 1; d <= Math.min(today.getDate(), daysInMonth); d++) {
    const key = `${year}-${String(monthNum).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const stored = localStorage.getItem(`prayers_${key}`);
    totalDays++;
    const prayers = stored ? JSON.parse(stored) : {};
    prayerNames.forEach(p => {
      localRates[p.key] = (localRates[p.key] || 0) + (prayers[p.key] ? 1 : 0);
    });
  }

  const rates = alltime?.prayer_rates || (
    totalDays > 0
      ? Object.fromEntries(prayerNames.map(p => [p.key, parseFloat((localRates[p.key] / totalDays).toFixed(2))]))
      : {}
  );

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="space-y-3">
        {prayerNames.map(prayer => {
          const rate = rates[prayer.key] ?? 0;
          const pct = Math.round(rate * 100);
          const col = PRAYER_COLORS[prayer.key];

          return (
            <div key={prayer.key} className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {/* Prayer label */}
              <div className={`w-16 flex-shrink-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                <span className={`text-xs font-semibold ${isDarkMode ? col.darkText : col.text}`}>
                  {t(`prayers.${prayer.key}`)}
                </span>
              </div>
              {/* Bar track */}
              <div className={`flex-1 h-2.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct}%`,
                    background: col.bar,
                    transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                  }}
                />
              </div>
              {/* Percentage */}
              <div className="w-9 flex-shrink-0 text-right">
                <span className={`text-xs font-bold tabular-nums ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* All-time stats footer */}
      {alltime && (
        <div className={`flex gap-4 mt-4 pt-3 border-t ${isDarkMode ? 'border-white/8' : 'border-gray-100'} ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="text-center flex-1">
            <p className={`text-2xl font-bold tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
              >
              {alltime.best_streak}
            </p>
            <p className={`text-[10px] uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
              {t('tracker.bestStreak')}
            </p>
          </div>
          <div className={`w-px ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`} />
          <div className="text-center flex-1">
            <p className={`text-2xl font-bold tabular-nums ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
              >
              {alltime.total_prayers.toLocaleString()}
            </p>
            <p className={`text-[10px] uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
              {t('tracker.totalPrayers')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrayerBreakdown;
